import { useEffect, useRef } from 'react';

const logos = [
  { name: 'Airbus', url: '/logoscroll/airbus.svg' },
  { name: 'Antwerp', url: '/logoscroll/antwerp.svg' },
  { name: 'Hostmi', url: '/logoscroll/hostmi.png' },
  { name: 'IDQ', url: '/logoscroll/idq.webp' },
  { name: 'Lazeriu', url: '/logoscroll/lazeriu.png' },
  { name: 'Surrey', url: '/logoscroll/surrey.svg' },
  { name: 'Tesat', url: '/logoscroll/tesat.png' },
  { name: 'Toshiba', url: '/logoscroll/toshiba.png' }
];

export default function LogoScroll() {
  const primaryRef = useRef<HTMLDivElement>(null);
  const secondaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const primary = primaryRef.current;
    const secondary = secondaryRef.current;
    if (!primary || !secondary) return;

    // Clone the first few items to the end for seamless loop
    const cloneItems = () => {
      const items = primary.getElementsByClassName('logo-item');
      const visibleWidth = primary.offsetWidth;
      let totalWidth = 0;
      const itemsToClone = [];

      // Calculate how many items we need to clone
      for (let i = 0; i < items.length; i++) {
        const item = items[i] as HTMLElement;
        totalWidth += item.offsetWidth;
        itemsToClone.push(item.cloneNode(true));
        if (totalWidth > visibleWidth) break;
      }

      // Add cloned items to secondary container
      secondary.innerHTML = '';
      itemsToClone.forEach(item => secondary.appendChild(item));
    };

    cloneItems();

    // Recalculate on resize
    window.addEventListener('resize', cloneItems);
    return () => window.removeEventListener('resize', cloneItems);
  }, []);

  return (
    <div className="py-0 bg-quantum-dark relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-center font-tokyo text-2xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-8">
          Trusted by Industry Leaders
        </h2>
      </div>

      <div className="relative w-full">
        {/* Gradient overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-quantum-dark to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-quantum-dark to-transparent z-10" />

        {/* Logo scroll container */}
        <div className="overflow-hidden">
          <div className="logo-track">
            {/* Primary container */}
            <div ref={primaryRef} className="logo-container">
              {logos.map((logo, index) => (
                <div
                  key={`${logo.name}-${index}`}
                  className="logo-item mx-6 md:mx-12 flex-none transition-transform duration-300 hover:scale-110 w-[120px] md:w-[160px]"
                >
                  <img
                    src={logo.url}
                    alt={logo.name}
                    className="h-12 md:h-16 w-auto object-contain filter brightness-0 invert opacity-70 hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              ))}
            </div>
            {/* Secondary container for seamless loop */}
            <div ref={secondaryRef} className="logo-container" />
          </div>
        </div>
      </div>

      <style>
        {`
          .logo-track {
            display: flex;
            width: fit-content;
            animation: scroll 40s linear infinite;
          }

          .logo-container {
            display: flex;
            align-items: center;
          }

          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-100%);
            }
          }

          @media (max-width: 768px) {
            .logo-track {
              animation: scroll 20s linear infinite;
            }
          }
        `}
      </style>
    </div>
  );
}